import auth from "./auth";

interface InactivityConfig {
  timeoutMinutes: number;
  warningMinutes: number;
}

type InactivityCallback = () => void;

class Inactivity {
  private timeoutMinutes: number;
  private warningMinutes: number;
  private timeoutId: number | null;
  private warningId: number | null;
  private onWarningCallback: InactivityCallback | null;
  private onTimeoutCallback: InactivityCallback | null;
  private isActive: boolean;
  private events: string[];
  private warned: boolean;
  private lastActivityAt: number;
  private boundReset: () => void;

  constructor(config: InactivityConfig = { timeoutMinutes: 30, warningMinutes: 2 }) {
    this.timeoutMinutes = config.timeoutMinutes;
    this.warningMinutes = config.warningMinutes;
    this.timeoutId = null;
    this.warningId = null;
    this.onWarningCallback = null;
    this.onTimeoutCallback = null;
    this.isActive = false;
    this.warned = false;
    this.lastActivityAt = 0;

    this.boundReset = this.onActivity.bind(this);

    // Events that indicate user activity
    this.events = [
      'mousedown',
      // mousemove is intentionally excluded, see onActivity()
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];
  }

  /**
   * Start monitoring for inactivity
   */
  start(onWarning?: InactivityCallback, onTimeout?: InactivityCallback): void {
    if (this.isActive) return;

    this.onWarningCallback = onWarning || null;
    this.onTimeoutCallback = onTimeout || this.defaultTimeout.bind(this);
    this.isActive = true;
    this.lastActivityAt = Date.now();

    // Set up activity listeners
    this.events.forEach(event => {
      window.addEventListener(event, this.boundReset, true);
    });

    // Start the clock
    this.resetTimer();
  }

  /**
   * Stop monitoring for inactivity
   */
  stop(): void {
    if (!this.isActive) return;

    this.isActive = false;
    this.clearTimers();

    // Remove activity listeners
    this.events.forEach(event => {
      window.removeEventListener(event, this.boundReset, true);
    });
  }

  /**
   * Called on user interaction.
   *
   * `mousemove` does not count as activity: a pointer drifting across the page
   * (or over the warning itself) is not intent to stay signed in, and letting
   * it reset the clock made the warning count down while the timeout kept
   * being pushed forward, so the session never actually expired.
   */
  private onActivity(e?: Event): void {
    if (!this.isActive) return;
    if (e && e.type === 'mousemove') return;
    this.resetTimer();
  }

  /**
   * Reset the inactivity clock.
   *
   * The deadline is derived from the timestamp of the last activity rather
   * than from accumulated `setTimeout` delays, so a throttled background tab
   * still expires rather than silently drifting past the deadline.
   */
  private resetTimer(): void {
    if (!this.isActive) return;

    this.clearTimers();
    this.lastActivityAt = Date.now();
    this.warned = false;

    const warningTime = (this.timeoutMinutes - this.warningMinutes) * 60 * 1000;
    const timeoutTime = this.timeoutMinutes * 60 * 1000;

    // Set warning timer
    this.warningId = window.setTimeout(() => {
      this.warn();
    }, warningTime);

    // Set timeout timer
    this.timeoutId = window.setTimeout(() => {
      this.expire();
    }, timeoutTime);
  }

  private warn(): void {
    if (!this.isActive || this.warned) return;
    this.warned = true;
    if (this.onWarningCallback) this.onWarningCallback();
  }

  private expire(): void {
    if (!this.isActive) return;
    this.stop();
    if (this.onTimeoutCallback) this.onTimeoutCallback();
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.warningId) {
      window.clearTimeout(this.warningId);
      this.warningId = null;
    }
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Default timeout action: logout the user
   */
  private async defaultTimeout(): Promise<void> {
    try {
      await auth.logout();
      // Redirect to login
      window.location.href = '/';
    } catch (e) {
      console.error('Error during inactivity timeout logout:', e);
    }
  }

  /**
   * Extend the session (call this when user clicks "Stay logged in")
   */
  extend(): void {
    this.resetTimer();
  }

  /**
   * Check if monitoring is active
   */
  get active(): boolean {
    return this.isActive;
  }

  /**
   * Milliseconds left before the session expires.
   */
  get remaining(): number {
    if (!this.isActive) return 0;
    const timeoutTime = this.timeoutMinutes * 60 * 1000;
    return Math.max(0, timeoutTime - (Date.now() - this.lastActivityAt));
  }

  /**
   * Update configuration
   */
  configure(config: Partial<InactivityConfig>): void {
    if (config.timeoutMinutes !== undefined) {
      this.timeoutMinutes = config.timeoutMinutes;
    }
    if (config.warningMinutes !== undefined) {
      this.warningMinutes = config.warningMinutes;
    }
    
    // If active, restart with new config
    if (this.isActive) {
      this.resetTimer();
    }
  }
}

// Export a singleton instance
export default new Inactivity();
